import React, { useEffect, useState } from 'react';
import { TrackResult, Badge } from '../../hooks/useGamification';

interface XPNotificationProps {
    reward: TrackResult | null;
    onClose: () => void;
    duration?: number;
}

/**
 * Minimal, clean XP notification toast
 * Appears briefly when user earns XP
 */
const XPNotification: React.FC<XPNotificationProps> = ({
    reward,
    onClose,
    duration = 3000
}) => {
    const [visible, setVisible] = useState(false);
    const [exiting, setExiting] = useState(false);

    useEffect(() => {
        if (reward && reward.success && reward.xp_earned) {
            setVisible(true);
            setExiting(false);

            const timer = setTimeout(() => {
                setExiting(true);
                setTimeout(() => {
                    setVisible(false);
                    onClose();
                }, 300);
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [reward, duration, onClose]);

    if (!visible || !reward) return null;

    return (
        <div
            style={{
                position: 'fixed',
                bottom: '24px',
                right: '24px',
                zIndex: 9999,
                animation: exiting
                    ? 'slideOutRight 0.3s ease-out forwards'
                    : 'slideInRight 0.3s ease-out forwards',
            }}
        >
            <style>
                {`
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOutRight {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
        `}
            </style>

            <div
                style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    color: 'white',
                    minWidth: '200px',
                }}
            >
                {/* XP Badge */}
                <div
                    style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        fontWeight: 700,
                    }}
                >
                    +{reward.xp_earned}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                    <div style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        marginBottom: '2px'
                    }}>
                        XP Gagné !
                    </div>

                    {reward.badge_awarded && (
                        <div style={{
                            fontSize: '12px',
                            opacity: 0.9,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}>
                            {reward.badge_awarded.emoji} Badge "{reward.badge_awarded.name}"
                        </div>
                    )}

                    {reward.level && (
                        <div style={{
                            fontSize: '11px',
                            opacity: 0.7,
                            marginTop: '2px',
                        }}>
                            Niveau {reward.level.level} • {reward.total_xp} XP total
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default XPNotification;
